/**
 * Local Files — client-side executor (File System Access API).
 *
 * "The brain stays on the server, the hands stay in the client": when the
 * backend yields an SSE `local_tool_call` event, ChatInterfaceFull runs the
 * tool HERE (against the folder the user granted) and POSTs the result back
 * as a `local_tool.result` action. See docs/LOCAL_FILES_DESIGN.md.
 *
 * Folder handles are persisted in IndexedDB, one per thread (1 thread = 1
 * folder). The handle itself never leaves the browser.
 *
 * Permission notes: `showDirectoryPicker()` and `requestPermission()` require
 * a user gesture — they can only run from the 📁 chip click. Tool execution
 * (no gesture available) relies on `queryPermission()` and reports
 * NEED_PERMISSION so the model can ask the user to click the chip.
 */

const DB_NAME = 'cme-local-files';
// v1: handles store. v2: + trusted store (v2 was briefly shipped WITHOUT the
// trusted-store creation — a hot-reload ran the 1→2 upgrade in that window,
// leaving the DB at v2 with no 'trusted' store). v3: clean bump so the
// idempotent upgrade below creates it; plus openDb self-heals any stuck state.
const DB_VERSION = 3;
const STORE = 'handles';
const TRUSTED_STORE = 'trusted'; // trusted folders (auto-approved writes)

export const MAX_READ_BYTES = 5 * 1024 * 1024; // 5 MB (mirrors backend)
export const DEFAULT_READ_BYTES = 200_000;
export const MAX_WRITE_BYTES = 10 * 1024 * 1024; // 10 MB (mirrors backend)
export const MAX_LIST_ENTRIES = 500;
export const MAX_LIST_DEPTH = 3;
// Structured extraction (xlsx/pdf/docx) — parsed client-side, never uploaded
export const MAX_PARSE_BYTES = 25 * 1024 * 1024; // 25 MB
export const MAX_EXTRACT_CHARS = 150_000;
export const DEFAULT_SHEET_ROWS = 100;
export const MAX_SHEET_ROWS = 2000;
export const DEFAULT_PDF_PAGES = 50;
// Materialization (local_upload_file → thread attachment) — mirrors backend
// MAX_UPLOAD_BYTES in cme/tools/handlers/local_files.py (media plan §3.4).
export const MAX_UPLOAD_BYTES = 10 * 1024 * 1024; // 10 MB
// Content search (local_search_files) — mirrors backend caps
export const MAX_SEARCH_MATCHES = 50;
export const MAX_SEARCH_MATCHES_PER_FILE = 10;
export const MAX_SEARCH_SNIPPET_CHARS = 200;
export const MAX_SEARCH_FILES = 2000;
export const MAX_SEARCH_FILE_BYTES = 2 * 1024 * 1024; // skip larger files
export const MAX_SEARCH_TOTAL_BYTES = 64 * 1024 * 1024; // overall scan budget
// Line-paged reading (local_read_file offset_line/max_lines) — mirrors backend
export const DEFAULT_READ_LINES = 400;
export const MAX_READ_LINES = 2000;
// The user reviews a diff card before a write lands: the client-side wait must
// expire just BEFORE the server-side 120s timeout (LOCAL_TOOL_TIMEOUTS).
export const WRITE_DECISION_TIMEOUT_MS = 115_000;

// threadId -> FileSystemDirectoryHandle (in-session cache over IndexedDB)
const handleCache = new Map();

/** Throw a DOMException-style AbortError if the user pressed Stop. */
function throwIfAborted(signal) {
  if (signal?.aborted) {
    const e = new Error('Aborted by the user (Stop)');
    e.name = 'AbortError';
    throw e;
  }
}

export function isLocalFilesSupported() {
  return typeof window !== 'undefined' && typeof window.showDirectoryPicker === 'function';
}

// --- Minimal IndexedDB helpers (no external deps) ---

function rawOpen(version) {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, version);
    // Idempotent upgrade: create any missing store (safe at any version)
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(STORE)) {
        req.result.createObjectStore(STORE, { keyPath: 'threadId' });
      }
      if (!req.result.objectStoreNames.contains(TRUSTED_STORE)) {
        req.result.createObjectStore(TRUSTED_STORE, { keyPath: 'id' });
      }
    };
    req.onsuccess = () => {
      // Never block another tab's DB upgrade: close our connection as soon
      // as a version change is requested.
      req.result.onversionchange = () => req.result.close();
      resolve(req.result);
    };
    req.onerror = () => reject(req.error);
    // Upgrade blocked by a stale connection: fail fast instead of hanging.
    req.onblocked = () => reject(new Error('DB_UPGRADE_BLOCKED: close other CME tabs and retry.'));
  });
}

async function openDb() {
  let db = await rawOpen(DB_VERSION);
  // Self-heal: if the on-disk DB somehow reached this version WITHOUT one of
  // the stores (e.g. an upgrade that ran from partial code), force one more
  // version bump — the idempotent upgrade creates whatever is missing.
  if (!db.objectStoreNames.contains(STORE) || !db.objectStoreNames.contains(TRUSTED_STORE)) {
    const repairVersion = db.version + 1;
    console.warn(`[local] IndexedDB missing stores at v${db.version} — repairing with upgrade to v${repairVersion}`);
    db.close();
    db = await rawOpen(repairVersion);
  }
  return db;
}

async function idbGetRecord(threadId) {
  const db = await openDb();
  try {
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, 'readonly');
      const req = tx.objectStore(STORE).get(threadId);
      req.onsuccess = () => resolve(req.result || null);
      req.onerror = () => reject(req.error);
    });
  } finally {
    db.close();
  }
}

async function idbPutRecord(record) {
  const db = await openDb();
  try {
    await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).put(record);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } finally {
    db.close();
  }
}

async function idbDeleteRecord(threadId) {
  const db = await openDb();
  try {
    await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).delete(threadId);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } finally {
    db.close();
  }
}

// --- Permission helpers ---

async function queryGranted(handle) {
  try {
    return (await handle.queryPermission({ mode: 'read' })) === 'granted';
  } catch {
    return false;
  }
}

// --- Trusted folders (auto-approved writes) ---
// Trust is per FOLDER (compared via isSameEntry), so it applies to every
// thread that connects the same folder on this machine.

async function idbGetAllTrusted() {
  const db = await openDb();
  try {
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(TRUSTED_STORE, 'readonly');
      const req = tx.objectStore(TRUSTED_STORE).getAll();
      req.onsuccess = () => resolve(req.result || []);
      req.onerror = () => reject(req.error);
    });
  } finally {
    db.close();
  }
}

async function idbPutTrusted(record) {
  const db = await openDb();
  try {
    await new Promise((resolve, reject) => {
      const tx = db.transaction(TRUSTED_STORE, 'readwrite');
      tx.objectStore(TRUSTED_STORE).put(record);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } finally {
    db.close();
  }
}

async function idbDeleteTrusted(id) {
  const db = await openDb();
  try {
    await new Promise((resolve, reject) => {
      const tx = db.transaction(TRUSTED_STORE, 'readwrite');
      tx.objectStore(TRUSTED_STORE).delete(id);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } finally {
    db.close();
  }
}

async function isTrustedFolder(handle) {
  let list;
  try {
    list = await idbGetAllTrusted();
  } catch (e) {
    // Surface real storage problems (e.g. a blocked v1→v2 upgrade) instead of
    // silently behaving as "not trusted" — that masks the failure forever.
    console.error('[local] cannot read trusted folders (IndexedDB):', e);
    return false;
  }
  for (const rec of list) {
    try {
      if (rec.handle && (await rec.handle.isSameEntry(handle))) return true;
    } catch {
      /* corrupt record — skip */
    }
  }
  return false;
}

/**
 * Trust / untrust the folder connected to a thread (shield toggle on the
 * chip, or the "always approve" checkbox on the write card). Returns the
 * VERIFIED resulting state — true only if the trust record is actually
 * readable back from IndexedDB.
 */
export async function setThreadFolderTrusted(threadId, trusted) {
  const stored = await getStoredHandle(threadId);
  if (!stored || !stored.handle) {
    console.warn('[local] trust toggle: no usable folder for this thread');
    return false;
  }
  try {
    if (trusted) {
      // Idempotent: drop any previous record for the same folder first
      await removeTrustedRecordsFor(stored.handle);
      await idbPutTrusted({
        id: (crypto.randomUUID ? crypto.randomUUID() : `t_${Date.now()}_${Math.random()}`),
        handle: stored.handle,
        name: stored.name,
        trustedAt: Date.now(),
      });
    } else {
      await removeTrustedRecordsFor(stored.handle);
    }
  } catch (e) {
    console.error('[local] trust toggle FAILED (IndexedDB):', e);
    return false;
  }
  const verified = await isTrustedFolder(stored.handle);
  console.debug('[local] trust toggle →', verified ? 'TRUSTED' : 'NOT trusted', `("${stored.name}")`);
  return verified;
}

async function removeTrustedRecordsFor(handle) {
  const list = await idbGetAllTrusted();
  for (const rec of list) {
    try {
      if (rec.handle && (await rec.handle.isSameEntry(handle))) {
        await idbDeleteTrusted(rec.id);
      }
    } catch {
      /* skip corrupt record */
    }
  }
}

/**
 * Get the stored handle for a thread and check (without prompting) that it's
 * still usable. Returns { handle, name, needsPermission, trusted } or null if
 * the thread has no folder.
 */
async function getStoredHandle(threadId) {
  const cached = handleCache.get(threadId);
  if (cached && (await queryGranted(cached))) {
    return { handle: cached, name: cached.name, needsPermission: false, trusted: await isTrustedFolder(cached) };
  }
  const record = await idbGetRecord(threadId).catch(() => null);
  if (!record || !record.handle) return null;
  const handle = record.handle;
  if (await queryGranted(handle)) {
    handleCache.set(threadId, handle);
    return { handle, name: handle.name || record.name, needsPermission: false, trusted: await isTrustedFolder(handle) };
  }
  return { handle, name: handle.name || record.name, needsPermission: true, trusted: await isTrustedFolder(handle) };
}

/**
 * Connect (or change) a folder for a thread. MUST be called from a user
 * gesture (the 📁 chip click) — the picker and the permission prompt both
 * require it. Returns the folder name, or null if the user cancelled/denied.
 */
export async function connectFolder(threadId) {
  if (!isLocalFilesSupported()) return null;

  // Re-connect path: a stored handle that lost its grant (yellow chip) needs
  // a fresh permission — no picker. Request readwrite (superset of read):
  // handles picked before the write feature can't be elevated and fall
  // through to a fresh picker below.
  const stored = await getStoredHandle(threadId);
  if (stored && stored.handle && stored.needsPermission) {
    try {
      const perm = await stored.handle.requestPermission({ mode: 'readwrite' });
      if (perm === 'granted') {
        handleCache.set(threadId, stored.handle);
        return stored.name;
      }
      // Denied or non-elevatable → fresh picker (user can cancel to keep the
      // old read-only binding).
    } catch {
      // Fall through to the picker.
    }
  }

  // Connect (no folder yet) or change (green chip click): directory picker.
  // The pick itself grants the session permission. readwrite because the
  // write tool needs it (each write still requires the user's diff approval).
  let handle;
  try {
    handle = await window.showDirectoryPicker({ mode: 'readwrite' });
  } catch {
    return null; // user cancelled the picker — keep the old binding
  }
  handleCache.set(threadId, handle);
  await idbPutRecord({ threadId, name: handle.name, handle, connectedAt: Date.now() }).catch(() => {});
  return handle.name;
}

/** Drop the folder binding for a thread (chip ✕). */
export async function disconnectFolder(threadId) {
  handleCache.delete(threadId);
  await idbDeleteRecord(threadId).catch(() => {});
}

/**
 * Chip state for a thread (no user gesture, never prompts).
 * Returns { name, needsPermission, trusted } | null.
 */
export async function getConnectedFolder(threadId) {
  if (!threadId) return null;
  const stored = await getStoredHandle(threadId);
  if (!stored) return null;
  return { name: stored.name, needsPermission: stored.needsPermission, trusted: stored.trusted };
}

// --- Path jail ---

/**
 * Normalize a model-supplied path to safe segments relative to the folder
 * root. Rejects absolute paths and "..".
 */
function jailPath(path) {
  let p = String(path ?? '').replace(/\\/g, '/').trim();
  if (/^[a-zA-Z]:/i.test(p) || p.startsWith('/') || p.startsWith('~')) {
    throw new Error(
      `INVALID_PATH: "${path}" is an absolute path. Use a path RELATIVE to the connected folder root (e.g. "Report/lug.csv").`
    );
  }
  const parts = p.split('/').filter((s) => s !== '' && s !== '.');
  if (parts.includes('..')) {
    throw new Error(`INVALID_PATH: ".." is not allowed. Paths must stay inside the connected folder.`);
  }
  return parts;
}

async function getDirHandle(root, parts) {
  let dir = root;
  for (const part of parts) {
    try {
      dir = await dir.getDirectoryHandle(part);
    } catch (e) {
      if (e && (e.name === 'NotFoundError' || e.name === 'TypeMismatchError')) {
        throw new Error(`PATH_NOT_FOUND: folder "${parts.join('/')}" does not exist in the connected folder.`);
      }
      throw e;
    }
  }
  return dir;
}

function formatSize(bytes) {
  if (bytes == null) return '?';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// --- Tool implementations ---

async function toolListDir(threadId, args, signal) {
  const stored = await requireUsableHandle(threadId);
  const parts = jailPath(args?.path);
  const rootDir = await getDirHandle(stored.handle, parts);

  const lines = [];
  let truncated = false;
  const queue = [{ dir: rootDir, prefix: parts.join('/'), depth: 0 }];

  while (queue.length > 0 && !truncated) {
    throwIfAborted(signal); // Stop must interrupt folder walks, not hang the UI
    const { dir, prefix, depth } = queue.shift();
    const subdirs = [];
    for await (const [name, entry] of dir.entries()) {
      if (name.startsWith('.')) continue; // skip dotfiles
      if (lines.length >= MAX_LIST_ENTRIES) {
        truncated = true;
        break;
      }
      const rel = prefix ? `${prefix}/${name}` : name;
      if (entry.kind === 'directory') {
        lines.push(`${rel}/ (dir)`);
        if (depth + 1 < MAX_LIST_DEPTH) subdirs.push({ dir: entry, prefix: rel, depth: depth + 1 });
      } else {
        let size = null;
        try {
          size = (await entry.getFile()).size;
        } catch {
          /* unreadable size — keep going */
        }
        lines.push(`${rel} (file, ${formatSize(size)})`);
      }
    }
    queue.push(...subdirs);
  }

  let out = `Contents of "${parts.join('/') || '.'}" (root: "${stored.name}", depth ≤ ${MAX_LIST_DEPTH}):\n`;
  out += lines.length > 0 ? lines.join('\n') : '(empty folder)';
  if (truncated) out += `\n[... truncated at ${MAX_LIST_ENTRIES} entries]`;
  return out;
}

async function toolReadFile(threadId, args) {
  const stored = await requireUsableHandle(threadId);
  const parts = jailPath(args?.path);
  if (parts.length === 0) {
    throw new Error('INVALID_PATH: a file path is required (e.g. "Report/lug.csv").');
  }

  const fileName = parts[parts.length - 1];
  const parentDir = await getDirHandle(stored.handle, parts.slice(0, -1));
  let fileHandle;
  try {
    fileHandle = await parentDir.getFileHandle(fileName);
  } catch (e) {
    if (e && (e.name === 'NotFoundError' || e.name === 'TypeMismatchError')) {
      throw new Error(`PATH_NOT_FOUND: file "${parts.join('/')}" does not exist in the connected folder.`);
    }
    throw e;
  }

  const file = await fileHandle.getFile();
  if (file.size === 0) return `(empty file: ${parts.join('/')})`;

  // --- Line-paged mode (offset_line / max_lines) -------------------------
  // Lets the model page through big text files deterministically instead of
  // hitting the byte cap. Enabled when either line param is present.
  const reqOffsetLine = Number(args?.offset_line);
  const reqMaxLines = Number(args?.max_lines);
  const lineMode =
    (Number.isFinite(reqMaxLines) && reqMaxLines > 0) ||
    (Number.isFinite(reqOffsetLine) && reqOffsetLine > 1);
  if (lineMode) {
    if (file.size > MAX_PARSE_BYTES) {
      throw new Error(
        `TOO_LARGE: file is ${formatSize(file.size)}, line paging supports up to ${formatSize(MAX_PARSE_BYTES)}.`
      );
    }
    let text;
    try {
      text = await file.text();
    } catch (e) {
      throw new Error(`READ_FAILED: could not decode "${parts.join('/')}" as text (${e?.message || e}).`);
    }
    if (text.includes('\u0000')) {
      throw new Error(
        'BINARY_FILE: this looks like a binary file. Use the dedicated tools ' +
          '(local_read_spreadsheet/pdf/docx) or local_upload_file to materialize it.'
      );
    }
    const lines = text.split('\n');
    const total = lines.length;
    const maxLines = Math.min(
      Number.isFinite(reqMaxLines) && reqMaxLines > 0 ? Math.floor(reqMaxLines) : DEFAULT_READ_LINES,
      MAX_READ_LINES
    );
    const from = Math.min(
      Math.max(1, Number.isFinite(reqOffsetLine) && reqOffsetLine > 0 ? Math.floor(reqOffsetLine) : 1),
      total
    );
    const to = Math.min(from - 1 + maxLines, total);
    let out =
      `File "${parts.join('/')}" (${formatSize(file.size)}) — lines ${from}–${to} of ${total}` +
      (total > 1 && lines[total - 1] === '' ? ' (last line empty)' : '') +
      ':\n\n' +
      lines.slice(from - 1, to).join('\n');
    if (to < total) {
      out += `\n\n[... ${total - to} more lines — call again with offset_line=${to + 1} (max_lines up to ${MAX_READ_LINES})]`;
    }
    return capExtractedText(out);
  }

  // --- Byte-cap mode (default, unchanged) --------------------------------
  const requested = Number(args?.max_bytes);
  const limit = Math.min(
    Number.isFinite(requested) && requested > 0 ? requested : DEFAULT_READ_BYTES,
    MAX_READ_BYTES
  );

  // Binary sniff on the first chunk (NUL byte or many control chars → base64).
  const headBuf = await file.slice(0, Math.min(8000, limit, file.size)).arrayBuffer();
  const head = new Uint8Array(headBuf);
  let control = 0;
  for (let i = 0; i < head.length; i++) {
    const b = head[i];
    if (b === 0) { control = head.length; break; }
    if (b < 9 || (b > 13 && b < 32)) control++;
  }
  const isBinary = head.length > 0 && control / head.length > 0.05;

  const slice = file.slice(0, limit);
  const truncatedNote =
    file.size > limit
      ? `\n[... truncated: file is ${formatSize(file.size)}, returned first ${formatSize(limit)} — page through it with offset_line/max_lines]`
      : '';

  if (isBinary) {
    const b64 = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result).split(',', 2)[1] || '');
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(slice);
    });
    return (
      `BASE64 (file: ${parts.join('/')}, mime: ${file.type || 'unknown'}, ` +
      `total ${formatSize(file.size)}, first ${formatSize(limit)} encoded):\n${b64}${truncatedNote}`
    );
  }

  const text = await slice.text();
  return text + truncatedNote;
}

// ---------------------------------------------------------------------------
// local_search_files — grep-style content search across the connected folder
// (Phase 1b: search-first reading). Returns ONLY path:line + short snippets;
// caps everywhere so a huge folder can never hang the tab.
// ---------------------------------------------------------------------------

/** Translate a simple glob (*, **, ?) to a RegExp over the relative path. */
function globToRegExp(glob) {
  let re = '';
  for (let i = 0; i < glob.length; i++) {
    const c = glob[i];
    if (c === '*') {
      if (glob[i + 1] === '*') {
        re += '.*';
        i++;
      } else {
        re += '[^/]*';
      }
    } else if (c === '?') {
      re += '[^/]';
    } else {
      re += c.replace(/[.+^${}()|[\]\\]/g, '\\$&');
    }
  }
  return new RegExp(`^${re}$`, 'i');
}

function searchSnippet(line, matchIdx) {
  const trimmed = line.trim();
  if (trimmed.length <= MAX_SEARCH_SNIPPET_CHARS) return trimmed;
  const start = Math.max(0, Math.min(matchIdx - 80, trimmed.length - MAX_SEARCH_SNIPPET_CHARS));
  const win = trimmed.slice(start, start + MAX_SEARCH_SNIPPET_CHARS);
  return (start > 0 ? '…' : '') + win + (start + MAX_SEARCH_SNIPPET_CHARS < trimmed.length ? '…' : '');
}

async function toolSearchFiles(threadId, args, signal) {
  const stored = await requireUsableHandle(threadId);
  const query = String(args?.query ?? '').trim();
  if (query.length < 2) {
    throw new Error('INVALID_QUERY: provide at least 2 characters to search for.');
  }
  const needle = query.toLowerCase();
  const glob = String(args?.glob ?? '').trim();
  // A glob without "/" (e.g. "*.csv") matches the BASENAME anywhere in the
  // tree — that's what "search the csv files" means in practice. A glob with
  // "/" (e.g. "Report/**") matches the full relative path.
  const globIsBasename = glob && !glob.includes('/');
  const globRe = glob ? globToRegExp(glob.replace(/\\/g, '/')) : null;

  const results = [];
  let filesScanned = 0;
  let filesSkippedSize = 0;
  let filesSkippedBinary = 0;
  let truncated = false;
  let budget = MAX_SEARCH_TOTAL_BYTES;

  const queue = [{ dir: stored.handle, prefix: '' }];
  while (queue.length > 0 && !truncated) {
    throwIfAborted(signal); // Stop must interrupt folder walks, not hang the UI
    const { dir, prefix } = queue.shift();
    let entries = [];
    try {
      for await (const [name, entry] of dir.entries()) entries.push([name, entry]);
    } catch {
      continue; // unreadable dir — skip, keep searching
    }
    for (const [name, entry] of entries) {
      throwIfAborted(signal);
      if (name.startsWith('.')) continue; // dotfiles/dirs (incl. .git)
      const rel = prefix ? `${prefix}/${name}` : name;
      if (entry.kind === 'directory') {
        queue.push({ dir: entry, prefix: rel });
        continue;
      }
      if (globRe && !globRe.test(globIsBasename ? name : rel)) continue;
      if (filesScanned >= MAX_SEARCH_FILES) {
        truncated = true;
        break;
      }
      filesScanned++;
      let file;
      try {
        file = await entry.getFile();
      } catch {
        continue;
      }
      if (file.size === 0 || file.size > MAX_SEARCH_FILE_BYTES) {
        if (file.size > MAX_SEARCH_FILE_BYTES) filesSkippedSize++;
        continue;
      }
      budget -= file.size;
      if (budget < 0) {
        truncated = true;
        break;
      }
      let text;
      try {
        text = await file.text();
      } catch {
        continue;
      }
      if (text.includes('\u0000')) {
        filesSkippedBinary++;
        continue;
      }
      const lines = text.split('\n');
      let fileMatches = 0;
      for (let ln = 0; ln < lines.length; ln++) {
        const idx = lines[ln].toLowerCase().indexOf(needle);
        if (idx === -1) continue;
        results.push(`${rel}:${ln + 1}: ${searchSnippet(lines[ln], idx)}`);
        fileMatches++;
        if (results.length >= MAX_SEARCH_MATCHES || fileMatches >= MAX_SEARCH_MATCHES_PER_FILE) break;
      }
      if (results.length >= MAX_SEARCH_MATCHES) {
        truncated = true;
        break;
      }
    }
  }

  let out =
    `Search "${query}"${glob ? ` in "${glob}"` : ''} — ${results.length} match(es), ` +
    `${filesScanned} file(s) scanned, root "${stored.name}":\n` +
    `(content search: images and other binaries cannot be searched inside — ` +
    `no matches does NOT mean a file is missing; find files by NAME with local_list_dir)\n\n`;
  out += results.length > 0 ? results.join('\n') : '(no matches)';
  const notes = [];
  if (truncated) notes.push(`results capped at ${MAX_SEARCH_MATCHES} — narrow with a glob or a longer query`);
  if (filesSkippedSize) notes.push(`${filesSkippedSize} file(s) > ${formatSize(MAX_SEARCH_FILE_BYTES)} skipped`);
  if (filesSkippedBinary) notes.push(`${filesSkippedBinary} binary file(s) skipped`);
  if (notes.length) out += `\n(${notes.join('; ')})`;
  return capExtractedText(out);
}


async function toolConnectFolder(threadId) {
  const stored = await getStoredHandle(threadId);
  if (!stored) {
    return {
      ok: false,
      error:
        'NO_FOLDER_CONNECTED: no local folder is connected to this chat. ' +
        'Ask the user to click the folder icon (📁) next to the message input, ' +
        'pick a folder (Chrome/Edge only) and grant access, then try again.',
    };
  }
  if (stored.needsPermission) {
    return {
      ok: false,
      error:
        'NEED_PERMISSION: the folder permission must be re-granted. Ask the user to click the ' +
        'folder chip (📁, yellow) next to the message input and allow access, then try again.',
    };
  }
  return { ok: true, result: `Folder connected: "${stored.name}". Paths are relative to this folder.` };
}

// ---------------------------------------------------------------------------
// Structured extraction (xlsx / pdf / docx) — parsed IN THE BROWSER with
// SheetJS / pdf.js / mammoth (dynamic imports). The raw files never leave
// the user's machine; only the extracted text flows back as a tool result.
// ---------------------------------------------------------------------------

/** Resolve a jailed path to an existing File inside the connected folder. */
async function resolveFileInFolder(threadId, rawPath) {
  const stored = await requireUsableHandle(threadId);
  const parts = jailPath(rawPath);
  if (parts.length === 0) {
    throw new Error('INVALID_PATH: a file path is required (e.g. "Report/data.xlsx").');
  }
  const fileName = parts[parts.length - 1];
  const parentDir = await getDirHandle(stored.handle, parts.slice(0, -1));
  let fileHandle;
  try {
    fileHandle = await parentDir.getFileHandle(fileName);
  } catch (e) {
    if (e && (e.name === 'NotFoundError' || e.name === 'TypeMismatchError')) {
      throw new Error(`PATH_NOT_FOUND: file "${parts.join('/')}" does not exist in the connected folder.`);
    }
    throw e;
  }
  const file = await fileHandle.getFile();
  if (file.size > MAX_PARSE_BYTES) {
    throw new Error(`TOO_LARGE: file is ${formatSize(file.size)}, max ${formatSize(MAX_PARSE_BYTES)} for extraction.`);
  }
  return { stored, parts, file };
}

function capExtractedText(text) {
  if (text.length > MAX_EXTRACT_CHARS) {
    return text.slice(0, MAX_EXTRACT_CHARS) + '\n\n[... truncated for context limits — narrow the range (max_rows / pages) and read again if you need more]';
  }
  return text;
}

async function toolReadSpreadsheet(threadId, args) {
  const { parts, file } = await resolveFileInFolder(threadId, args?.path);
  const XLSX = await import('xlsx');
  const wb = XLSX.read(await file.arrayBuffer(), { cellDates: true });

  const sheetNames = wb.SheetNames;
  if (sheetNames.length === 0) throw new Error('EMPTY_WORKBOOK: the spreadsheet has no sheets.');
  const requestedSheet = args?.sheet_name;
  let sheetName = sheetNames[0];
  let sheetNote = '';
  if (requestedSheet && requestedSheet !== sheetName) {
    const found = sheetNames.find((n) => n.toLowerCase() === String(requestedSheet).toLowerCase());
    if (found) {
      sheetName = found;
    } else {
      sheetNote = `\n(No sheet named "${requestedSheet}" — using the first sheet. Available: ${sheetNames.join(', ')})`;
    }
  }

  const requestedRows = Number(args?.max_rows);
  const maxRows = Math.min(
    Number.isFinite(requestedRows) && requestedRows > 0 ? Math.floor(requestedRows) : DEFAULT_SHEET_ROWS,
    MAX_SHEET_ROWS
  );
  // start_row: zero-based index into DATA rows (header excluded) — lets the
  // model page through sheets longer than MAX_SHEET_ROWS.
  const requestedStart = Number(args?.start_row);
  const startRow = Number.isFinite(requestedStart) && requestedStart > 0 ? Math.floor(requestedStart) : 0;

  const csv = XLSX.utils.sheet_to_csv(wb.Sheets[sheetName], { blankrows: false });
  const lines = csv.split('\n');
  const totalDataRows = Math.max(lines.length - 1, 0); // first line is the header
  const fromRow = Math.min(startRow, totalDataRows);
  const toRow = Math.min(fromRow + maxRows, totalDataRows);
  const shown = [lines[0], ...lines.slice(1 + fromRow, 1 + toRow)];

  let out =
    `Spreadsheet "${parts.join('/')}" (${formatSize(file.size)})\n` +
    `Sheets: ${sheetNames.join(', ')}\n` +
    `Sheet "${sheetName}" as CSV — data rows ${fromRow}–${toRow - 1} of ${totalDataRows}` +
    sheetNote + '\n\n' + shown.join('\n');
  if (toRow < totalDataRows) {
    out += `\n\n[... ${totalDataRows - toRow} more rows after row ${toRow - 1} — the file is NOT truncated, this is a per-call page. Call again with start_row=${toRow} (and max_rows up to ${MAX_SHEET_ROWS}) to read the next page.]`;
  }
  return capExtractedText(out);
}

let pdfjsWorkerReady = false;

async function toolReadPdf(threadId, args) {
  const { parts, file } = await resolveFileInFolder(threadId, args?.path);
  const pdfjs = await import('pdfjs-dist');
  if (!pdfjsWorkerReady) {
    // Vite ?worker import → dedicated worker chunk, main bundle untouched
    const workerMod = await import('pdfjs-dist/build/pdf.worker.min.mjs?worker');
    pdfjs.GlobalWorkerOptions.workerPort = new workerMod.default();
    pdfjsWorkerReady = true;
  }

  const doc = await pdfjs.getDocument({ data: new Uint8Array(await file.arrayBuffer()) }).promise;
  try {
    const total = doc.numPages;
    const from = Math.max(1, Math.floor(Number(args?.from_page) || 1));
    const requestedTo = Number(args?.to_page);
    const to = Math.min(
      total,
      Number.isFinite(requestedTo) && requestedTo >= from
        ? Math.floor(requestedTo)
        : from - 1 + DEFAULT_PDF_PAGES
    );

    const chunks = [];
    let extractedChars = 0;
    for (let p = from; p <= to; p++) {
      const page = await doc.getPage(p);
      const tc = await page.getTextContent();
      let text = '';
      for (const item of tc.items) {
        text += item.str;
        if (item.hasEOL) text += '\n';
      }
      extractedChars += text.trim().length;
      chunks.push(`=== Page ${p}/${total} ===\n${text.trim() || '(no text on this page)'}`);
    }

    if (extractedChars === 0) {
      throw new Error(
        'SCANNED_PDF: this PDF has no text layer (likely scanned images). ' +
          'Ask the user for a text-based version, or offer to work from the file names instead.'
      );
    }

    let out = `PDF "${parts.join('/')}" — ${total} pages, showing ${from}–${to}:\n\n` + chunks.join('\n\n');
    if (to < total) {
      out += `\n\n[... ${total - to} more pages — call again with from_page=${to + 1}]`;
    }
    return capExtractedText(out);
  } finally {
    doc.destroy().catch(() => {});
  }
}

async function toolReadDocx(threadId, args) {
  const { parts, file } = await resolveFileInFolder(threadId, args?.path);
  const mammoth = await import('mammoth');
  const result = await mammoth.extractRawText({ arrayBuffer: await file.arrayBuffer() });
  const text = (result.value || '').trim();
  if (!text) throw new Error('EMPTY_DOCUMENT: no extractable text found in this .docx.');
  return capExtractedText(
    `Word document "${parts.join('/')}" (${formatSize(file.size)}) as plain text:\n\n${text}`
  );
}


/** Returns a usable handle or throws a coded error for the model. */
async function requireUsableHandle(threadId) {
  const stored = await getStoredHandle(threadId);
  if (!stored) {
    throw new Error(
      'NO_FOLDER_CONNECTED: no local folder is connected to this chat. ' +
        'Ask the user to click the folder icon (📁) next to the message input, ' +
        'pick a folder (Chrome/Edge only) and grant access, then try again.'
    );
  }
  if (stored.needsPermission) {
    throw new Error(
      'NEED_PERMISSION: the folder permission must be re-granted. Ask the user to click the ' +
        'folder chip (📁, yellow) next to the message input and allow access, then try again.'
    );
  }
  return stored;
}

// ---------------------------------------------------------------------------
// local_upload_file — materialize a local file as a THREAD ATTACHMENT
// (docs/MEDIA_ARCHITECTURE_PLAN.md §3.1). The browser reads the bytes and
// uploads them via the authenticated multipart path the chat already uses;
// only the handle string ("UPLOADED: attachment_id=...") ever reaches the
// conversation — never the bytes.
// ---------------------------------------------------------------------------

async function toolUploadFile(threadId, args) {
  const stored = await requireUsableHandle(threadId);
  const parts = jailPath(args?.path);
  if (parts.length === 0) {
    throw new Error('INVALID_PATH: a file path is required (e.g. "Photos/vacanza.jpg").');
  }
  const fileName = parts[parts.length - 1];
  const parentDir = await getDirHandle(stored.handle, parts.slice(0, -1));
  let fileHandle;
  try {
    fileHandle = await parentDir.getFileHandle(fileName);
  } catch (e) {
    if (e && (e.name === 'NotFoundError' || e.name === 'TypeMismatchError')) {
      throw new Error(`PATH_NOT_FOUND: file "${parts.join('/')}" does not exist in the connected folder.`);
    }
    throw e;
  }

  const file = await fileHandle.getFile();
  if (file.size === 0) {
    throw new Error('EMPTY_FILE: the file is empty (0 bytes).');
  }
  if (file.size > MAX_UPLOAD_BYTES) {
    throw new Error(
      `TOO_LARGE: file is ${formatSize(file.size)}, max ${formatSize(MAX_UPLOAD_BYTES)} for upload.`
    );
  }

  const API_URL = window.INSIGHT_CONFIG?.API_URL ?? '';
  const token = localStorage.getItem('cme_token');
  const formData = new FormData();
  formData.append(
    'payload',
    JSON.stringify({ type: 'attachments.upload', params: { thread_id: threadId } })
  );
  formData.append('file_0', file, file.name);

  const res = await fetch(`${API_URL}/cme`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
  });
  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try {
      detail = (await res.json()).error || detail;
    } catch {
      /* keep HTTP detail */
    }
    throw new Error(`UPLOAD_FAILED: ${detail}`);
  }
  const data = await res.json();
  const att = data?.attachments?.[0];
  if (!att?.attachment_id) {
    throw new Error('UPLOAD_FAILED: the server did not return an attachment id.');
  }
  return (
    `UPLOADED: attachment_id=${att.attachment_id} filename=${att.filename} size=${att.size}.\n` +
    `You can embed it in PDF/Word exports as ![caption](attachment:${att.attachment_id}), ` +
    `or attach it to an email using its id.\n` +
    `Keep this id: for any later use of the same file in this conversation, reuse it — do not upload the file again.`
  );
}

/**
 * Execute a local tool call coming from the backend.
 * Returns { ok: true, result: string } or { ok: false, error: string }.
 * `signal` (AbortSignal): when the user presses Stop, walkers bail out by
 * throwing AbortError — rethrown (not converted to a tool error) so the
 * chat run() generator unwinds immediately.
 */
export async function executeLocalTool(tool, args, threadId, signal) {
  try {
    if (!isLocalFilesSupported()) {
      return {
        ok: false,
        error: 'UNSUPPORTED_BROWSER: local file access requires Chrome or Edge.',
      };
    }
    if (!threadId) {
      return { ok: false, error: 'CLIENT_ERROR: no active thread.' };
    }
    switch (tool) {
      case 'local_connect_folder':
        return await toolConnectFolder(threadId);
      case 'local_list_dir':
        return { ok: true, result: await toolListDir(threadId, args, signal) };
      case 'local_read_file':
        return { ok: true, result: await toolReadFile(threadId, args) };
      case 'local_read_spreadsheet':
        return { ok: true, result: await toolReadSpreadsheet(threadId, args) };
      case 'local_read_pdf':
        return { ok: true, result: await toolReadPdf(threadId, args) };
      case 'local_read_docx':
        return { ok: true, result: await toolReadDocx(threadId, args) };
      case 'local_upload_file':
        return { ok: true, result: await toolUploadFile(threadId, args) };
      case 'local_search_files':
        return { ok: true, result: await toolSearchFiles(threadId, args, signal) };
      case 'local_write_file':
        // Writes are handled by the dedicated prepare → confirm → apply flow
        // (they need the user's approval on a diff card before executing).
        return {
          ok: false,
          error: 'WRITE_NEEDS_CONFIRM_UI: local_write_file requires the confirm card flow.',
        };
      default:
        return { ok: false, error: `UNKNOWN_TOOL: "${tool}" is not a local tool.` };
    }
  } catch (e) {
    if (e?.name === 'AbortError') throw e; // Stop pressed — unwind, don't fake a tool error
    return { ok: false, error: e?.message ? String(e.message) : 'CLIENT_ERROR: execution failed.' };
  }
}

// ---------------------------------------------------------------------------
// local_write_file: prepare → user decision (diff card) → apply
// ---------------------------------------------------------------------------

// Pending write decisions: callId -> { resolve } (resolved by WriteConfirmCard)
// Settled outcomes: callId -> 'approved' | 'rejected' | 'auto'
//
// Both live on globalThis — NOT module scope — so every module instance shares
// the SAME maps. Vite HMR can re-evaluate this module while an in-flight run
// generator still holds the OLD instance: module-level Maps would split in
// two and the card's decision would never reach the awaiting promise (the
// "stuck after approve" bug). A window-level registry survives hot reloads.
const G = globalThis;
const pendingDecisions = (G.__cmeLocalPendingDecisions =
  G.__cmeLocalPendingDecisions || new Map());
const writeOutcomes = (G.__cmeLocalWriteOutcomes =
  G.__cmeLocalWriteOutcomes || new Map());
// Full decisions (approved + trustFolder) for the poll fallback below.
const writeDecisions = (G.__cmeLocalWriteDecisions =
  G.__cmeLocalWriteDecisions || new Map());

/** Outcome of a settled write card ('approved'|'rejected'|'auto'), or null. */
export function getWriteOutcome(callId) {
  return writeOutcomes.get(callId) ?? null;
}

/** Resolve a pending write decision (called by the WriteConfirmCard buttons). */
export function resolveWriteDecision(callId, approved, trustFolder = false) {
  writeOutcomes.set(callId, approved ? 'approved' : 'rejected');
  writeDecisions.set(callId, { approved, trustFolder });
  const entry = pendingDecisions.get(callId);
  if (entry) {
    pendingDecisions.delete(callId);
    entry.resolve({ approved, trustFolder });
  }
}

/**
 * Await the user's decision on a write. Resolves { approved, trustFolder },
 * or null on timeout. Rejects with AbortError when `signal` fires (Stop).
 *
 * Belt AND suspenders: besides the direct resolve, a 400ms poll watches the
 * shared writeDecisions map — even if the direct resolve is lost (module
 * re-evaluation, stale closure, missed entry) the decision is picked up
 * anyway. The map lives on globalThis precisely so every module instance
 * sees the same data.
 */
export function waitForWriteDecision(callId, timeoutMs = WRITE_DECISION_TIMEOUT_MS, signal) {
  return new Promise((resolve, reject) => {
    let settled = false;
    let poll = null;
    const startedAt = Date.now();
    const finish = (value) => {
      if (settled) return;
      settled = true;
      if (poll) clearInterval(poll);
      if (onAbort) signal?.removeEventListener('abort', onAbort);
      pendingDecisions.delete(callId);
      resolve(value);
    };
    const onAbort = () => {
      if (settled) return;
      settled = true;
      if (poll) clearInterval(poll);
      pendingDecisions.delete(callId);
      const e = new Error('Aborted by the user (Stop)');
      e.name = 'AbortError';
      reject(e);
    };
    if (signal) {
      if (signal.aborted) {
        onAbort();
        return;
      }
      signal.addEventListener('abort', onAbort, { once: true });
    }
    poll = setInterval(() => {
      const d = writeDecisions.get(callId);
      if (d) {
        finish(d);
      } else if (Date.now() - startedAt >= timeoutMs) {
        finish(null);
      }
    }, 400);
    pendingDecisions.set(callId, { resolve: finish });
  });
}

// Compact line diff (LCS) with common prefix/suffix trimming. Returns an
// array of { t: 'ctx'|'add'|'del', s: line } capped for display, or null
// when the files are too large to diff (caller shows a replace summary).
const DIFF_MAX_LINES = 400;

function lineDiff(oldText, newText) {
  const A = oldText.split('\n');
  const B = newText.split('\n');

  // Trim common prefix/suffix so the LCS matrix only covers the changed region
  let start = 0;
  while (start < A.length && start < B.length && A[start] === B[start]) start++;
  let endA = A.length;
  let endB = B.length;
  while (endA > start && endB > start && A[endA - 1] === B[endB - 1]) {
    endA--;
    endB--;
  }
  const midA = A.slice(start, endA);
  const midB = B.slice(start, endB);

  if (midA.length * midB.length > 4_000_000) return null; // too large → no diff

  // LCS table over the changed region
  const m = midA.length;
  const n = midB.length;
  const lcs = Array.from({ length: m + 1 }, () => new Uint32Array(n + 1));
  for (let i = m - 1; i >= 0; i--) {
    for (let j = n - 1; j >= 0; j--) {
      lcs[i][j] = midA[i] === midB[j] ? lcs[i + 1][j + 1] + 1 : Math.max(lcs[i + 1][j], lcs[i][j + 1]);
    }
  }

  const ops = [];
  let i = 0;
  let j = 0;
  while (i < m && j < n) {
    if (midA[i] === midB[j]) {
      ops.push({ t: 'ctx', s: midA[i] });
      i++;
      j++;
    } else if (lcs[i + 1][j] >= lcs[i][j + 1]) {
      ops.push({ t: 'del', s: midA[i] });
      i++;
    } else {
      ops.push({ t: 'add', s: midB[j] });
      j++;
    }
  }
  while (i < m) ops.push({ t: 'del', s: midA[i++] });
  while (j < n) ops.push({ t: 'add', s: midB[j++] });

  // Assemble the display: a few context lines around the changes
  const prefixCtx = A.slice(Math.max(0, start - 2), start);
  const suffixCtx = B.slice(endB, Math.min(B.length, endB + 2));
  const lines = [
    ...prefixCtx.map((s) => ({ t: 'ctx', s })),
    ...ops,
    ...suffixCtx.map((s) => ({ t: 'ctx', s })),
  ];

  let truncated = false;
  let out = lines;
  if (lines.length > DIFF_MAX_LINES) {
    out = lines.slice(0, DIFF_MAX_LINES);
    truncated = true;
  }
  return { lines: out, truncated };
}

/** Sniff whether existing file content is binary (same heuristic as read). */
async function isBinaryFile(file) {
  const buf = await file.slice(0, Math.min(8000, file.size)).arrayBuffer();
  const head = new Uint8Array(buf);
  let control = 0;
  for (let i = 0; i < head.length; i++) {
    const b = head[i];
    if (b === 0) return true;
    if (b < 9 || (b > 13 && b < 32)) control++;
  }
  return head.length > 0 && control / head.length > 0.05;
}

/**
 * Phase 1 — build the write proposal (path, sizes, diff). Nothing is written.
 * Returns { ok: true, proposal } or { ok: false, error }.
 */
export async function prepareLocalWrite(threadId, args) {
  try {
    if (!isLocalFilesSupported()) {
      return { ok: false, error: 'UNSUPPORTED_BROWSER: local file access requires Chrome or Edge.' };
    }
    const stored = await requireUsableHandle(threadId);
    const content = String(args?.content ?? '');
    const newBytes = new TextEncoder().encode(content).length;
    console.debug('[local-write] prepare — folder trusted:', stored.trusted);
    if (newBytes > MAX_WRITE_BYTES) {
      return {
        ok: false,
        error: `TOO_LARGE: content is ${formatSize(newBytes)}, max ${formatSize(MAX_WRITE_BYTES)}.`,
      };
    }

    const parts = jailPath(args?.path);
    if (parts.length === 0) {
      return { ok: false, error: 'INVALID_PATH: a file path is required (e.g. "output/report.md").' };
    }
    const fileName = parts[parts.length - 1];

    // Parent folders may not exist yet (write creates them) — a missing
    // parent simply means this is a new file.
    let parentDir = null;
    try {
      parentDir = await getDirHandle(stored.handle, parts.slice(0, -1));
    } catch (e) {
      if (!(e instanceof Error && e.message.startsWith('PATH_NOT_FOUND'))) throw e;
    }

    // Existing file (for the diff), if any
    let oldText = null;
    let oldIsBinary = false;
    let oldSize = 0;
    if (parentDir) {
      try {
        const fh = await parentDir.getFileHandle(fileName);
        const file = await fh.getFile();
        oldSize = file.size;
        if (oldSize > 0) {
          oldIsBinary = await isBinaryFile(file);
          if (!oldIsBinary) {
            oldText = await file.slice(0, Math.min(file.size, 2 * 1024 * 1024)).text();
          }
        } else {
          oldText = '';
        }
      } catch (e) {
        if (!(e && (e.name === 'NotFoundError' || e.name === 'TypeMismatchError'))) throw e;
        // new file
      }
    }

    // Trusted folders are auto-approved: skip the (pointless) diff computation,
    // the audit card shows a content preview instead.
    let diff = null;
    if (!stored.trusted && oldText !== null && !oldIsBinary) {
      diff = lineDiff(oldText, content);
    }

    return {
      ok: true,
      proposal: {
        path: parts.join('/'),
        isNew: oldText === null,
        oldIsBinary,
        oldSize,
        newSize: newBytes,
        trusted: stored.trusted,
        diff,
        preview: diff ? null : content.split('\n').slice(0, 40).join('\n'),
      },
    };
  } catch (e) {
    return { ok: false, error: e?.message ? String(e.message) : 'CLIENT_ERROR: write preparation failed.' };
  }
}

/**
 * Phase 3 — write the file (only after the user approved the diff card).
 * Must conceptually run close to the approval click: if the write permission
 * needs a (re-)grant, the click's user gesture is still fresh enough for
 * requestPermission in most cases. Returns { ok, result | error }.
 */
export async function applyLocalWrite(threadId, args) {
  try {
    const stored = await requireUsableHandle(threadId);
    const content = String(args?.content ?? '');
    const parts = jailPath(args?.path);
    if (parts.length === 0) {
      return { ok: false, error: 'INVALID_PATH: a file path is required.' };
    }

    // Ensure write permission (query first; request may use the click gesture).
    // NB: requestPermission can NEVER SETTLE when called outside a user
    // gesture (known Chrome behaviour) — race it with a short timeout and
    // fail with a clean error instead of hanging the whole chat stream.
    let perm = 'granted';
    try {
      perm = await stored.handle.queryPermission({ mode: 'readwrite' });
      if (perm !== 'granted') {
        perm = await Promise.race([
          stored.handle.requestPermission({ mode: 'readwrite' }),
          new Promise((r) => setTimeout(() => r('__timeout__'), 8000)),
        ]);
      }
    } catch {
      perm = 'denied';
    }
    if (perm !== 'granted') {
      return {
        ok: false,
        error:
          'NEED_PERMISSION_WRITE: the browser did not grant write access to the folder. ' +
          'Ask the user to click the 📁 folder chip next to the message input and allow ' +
          '"view and edit files" when prompted (or disconnect and reconnect the folder), ' +
          'then retry the write.',
      };
    }

    // Walk/create parent directories
    let dir = stored.handle;
    for (const part of parts.slice(0, -1)) {
      dir = await dir.getDirectoryHandle(part, { create: true });
    }
    const fileHandle = await dir.getFileHandle(parts[parts.length - 1], { create: true });
    const writable = await fileHandle.createWritable();
    await writable.write(content);
    await writable.close();

    const newBytes = new TextEncoder().encode(content).length;
    return {
      ok: true,
      result: `Write ${stored.trusted ? 'auto-approved (trusted folder)' : 'confirmed by the user'}: saved ${formatSize(newBytes)} to "${parts.join('/')}"`,
    };
  } catch (e) {
    return { ok: false, error: e?.message ? String(e.message) : 'CLIENT_ERROR: write failed.' };
  }
}
